import React, { useState, useRef, useEffect } from 'react';
import { CircleNotchIcon } from '@revfluence/fresh-icons/regular/esm';
import { RawDraftContentState } from 'draft-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useGetMessageTemplateById } from '@/app/hooks';
import { Editor } from '@/app/components';
import { Button } from '@/shadcn/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';

// Template content renderer using the full Editor component
const TemplateContentRenderer: React.FC<{ content: string }> = ({ content }) => {
  const [initialValue, setInitialValue] = useState<RawDraftContentState | null>(null);

  useEffect(() => {
    try {
      if (!content) return;

      let rawContentState: any;

      // Parse content if it's a string
      if (typeof content === 'string') {
        try {
          rawContentState = JSON.parse(content);
        } catch {
          // If not JSON, create a simple text block
          rawContentState = {
            blocks: [{
              key: 'simple',
              text: content,
              type: 'unstyled',
              depth: 0,
              inlineStyleRanges: [],
              entityRanges: [],
              data: {}
            }],
            entityMap: {}
          };
        }
      } else {
        rawContentState = content;
      }

      if (!rawContentState || !rawContentState.blocks) {
        return;
      }

      setInitialValue(rawContentState);
    } catch (error) {
      console.error('Error parsing content:', error);
    }
  }, [content]);

  if (!initialValue) {
    return <div className="text-gray-500 text-sm">Loading content...</div>;
  }

  return (
    <div className="template-content-preview">
      <div className="template-editor-wrapper">
        <style>
          {`
            .template-editor-wrapper .DraftEditor-root,
            .template-editor-wrapper .DraftEditor-editorContainer,
            .template-editor-wrapper .public-DraftEditor-content {
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
              padding: 0 !important;
              margin: 0 !important;
              overflow: visible !important;
              max-height: none !important;
              height: auto !important;
            }
            .template-editor-wrapper .public-DraftEditor-content div {
              word-wrap: break-word !important;
              margin-bottom: 8px !important;
              overflow: visible !important;
            }
            .template-editor-wrapper .public-DraftEditor-content div:last-child {
              margin-bottom: 0 !important;
            }
            .template-editor-wrapper {
              overflow: visible !important;
              max-height: none !important;
              height: auto !important;
            }
          `}
        </style>
        <Editor
          initialValue={initialValue}
          readOnly
          placeholder=""
          onChange={() => { }} // Read-only
          hasFixedHeight={false}
          className="overflow-visible"
        />
      </div>
    </div>
  );
};

// Template Preview Component
interface TemplatePreviewProps {
  template: {
    id: number;
    displayName: string;
  };
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template }) => {
  const { data: { template: templateData } = {}, loading } = useGetMessageTemplateById({
    variables: { templateId: template.id },
    skip: !template.id,
  });
  if (loading) {
    return (
      <div className="p-4 w-80">
        <CircleNotchIcon className="h-4 w-4 animate-spin mx-auto" aria-hidden="true" />
      </div>
    );
  }

  const emailContent = templateData?.text || '';

  return (
    <div className="rounded-2xl shadow-lg">
      <div className="px-3 py-2">
        <p className="font-medium text-primary text-sm m-0">Preview</p>
        <p className="font-medium text-primary text-xs m-0">{template.displayName || 'Untitled Template'}</p>
        <p className="font-normal text-primary text-xs m-0"><span className="font-semibold text-grey-5">Subject:</span> {templateData?.subject || 'Enter the subject here'}</p>
      </div>

      <div className="px-3 py-2">
        <TemplateContentRenderer content={emailContent} />
      </div>
    </div>
  );
};

interface TemplateHoverPreviewProps {
  template: {
    id: number;
    displayName: string;
  };
}

export const TemplateHoverPreview: React.FC<TemplateHoverPreviewProps> = ({ template }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const showPopover = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 200);
  };

  const hidePopover = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = undefined;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const cancelHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outlineHeader"
          size="smallIcon"
          aria-label="Preview template"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          onMouseEnter={showPopover}
          onMouseLeave={hidePopover}
          ref={buttonRef}
        >
          <FontAwesomeIcon icon={faEye} className="h-3 w-3 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="center"
        className="p-0 z-50 w-auto max-w-md"
        sideOffset={340}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={cancelHide}
        onMouseLeave={hidePopover}
        onInteractOutside={(e) => {
          const target = e.target as Element;
          if (!buttonRef.current?.contains(target)) setIsOpen(false);
        }}
      >
        <TemplatePreview template={template} />
      </PopoverContent>
    </Popover>
  );
};
